/**
 * Types du domaine catalogue.
 */
export interface CatalogItem {
  id: string;
  category: string;
  subcategory: string | null;
  name: string;
  description: string | null;
  reference: string | null;
  unit: string;
  unitCost: number;
  /** Absent = on vend au prix de revient, sans marge. */
  unitPrice: number | null;
  marginPercent: number | null;
  tvaRate: number;
  supplierId: string | null;
  isActive: boolean;
  /** Solde ajusté à la main ou par la réception d'une commande. `null` = non suivi. */
  stockQuantity: number | null;
  /** Seuil sous lequel une alerte se déclenche. `null` = pas d'alerte. */
  reorderThreshold: number | null;
}

/**
 * Cet article manque-t-il de stock ?
 *
 * `null` dans l'un ou l'autre champ veut dire « pas suivi » ou « pas de
 * seuil réglé » — jamais « en rupture ». Un article dont le stock n'est
 * simplement pas renseigné ne doit pas s'afficher comme critique.
 *
 * ⚠️ Cette règle double `check_low_stock()` en SQL, qui pose l'alerte **au
 * franchissement** du seuil. C'est assumé — la base doit pouvoir alerter sans
 * le TypeScript, comme `BILLABLE` et `recalc_project_costs` — mais les deux
 * doivent bouger ensemble. La différence de moment, elle, est voulue : le SQL
 * dit l'événement, cette fonction dit l'état, et le tableau de bord a besoin
 * du second parce qu'un article resté sous son seuil trois semaines ne
 * déclenche plus rien.
 */
export function isLowStock(
  item: Pick<CatalogItem, "stockQuantity" | "reorderThreshold">,
): boolean {
  return (
    item.stockQuantity != null &&
    item.reorderThreshold != null &&
    item.stockQuantity <= item.reorderThreshold
  );
}

/**
 * Taux de marque : part de marge dans le prix de vente.
 *
 * C'est la convention retenue dans toute l'application — les totaux d'un devis
 * calculent `(HT - coût) / HT`. Mélanger avec le taux de marge, qui rapporte
 * la même marge au coût d'achat, donnerait deux pourcentages différents pour
 * la même affaire et ferait douter le patron de ses propres chiffres.
 *
 * Renvoie null plutôt que 0 quand il n'y a pas de marge à afficher : « 0 % »
 * et « non renseigné » ne disent pas la même chose.
 */
export function computeMarginPercent(
  unitCost: number,
  unitPrice: number | null | undefined,
): number | null {
  const price = unitPrice ?? unitCost;
  if (price <= 0 || price <= unitCost) return null;
  return Math.round(((price - unitCost) / price) * 1000) / 10;
}

/** Prix de vente effectif : celui saisi, ou le prix de revient à défaut. */
export function effectivePrice(item: Pick<CatalogItem, "unitCost" | "unitPrice">): number {
  return item.unitPrice ?? item.unitCost;
}

/* ─── Les sorties de stock ──────────────────────────────────────────────── */

/**
 * Ce qu'un chantier a consommé du dépôt.
 *
 * Le patron : « on peut pas prendre du stock directement depuis un chantier,
 * c'est pourtant nécessaire pour connaître ses marges ». Chaque sortie écrit
 * une dépense sur le chantier, et c'est elle qui fait bouger la marge.
 *
 * `quantity` est **positive** ici : l'écran parle de ce qui est parti sur le
 * chantier, pas d'un solde. Le signe vit dans le journal, en base.
 */
export interface ProjectStockUsage {
  id: string;
  projectId: string;
  catalogItemId: string;
  itemName: string;
  itemUnit: string;
  quantity: number;
  /** Coût unitaire figé au moment de la sortie. `null` = article sans coût saisi. */
  unitCost: number | null;
  costHt: number;
  note: string | null;
  createdAt: string;
}

/**
 * Cet article peut-il sortir du stock ?
 *
 * Un article dont le stock n'est pas suivi n'a pas de solde à décrémenter, et
 * la fonction en base le refuse. Le dire avant le clic évite de découvrir la
 * règle par un message d'erreur — c'est le cas courant de la main-d'œuvre et
 * des prestations, qui peuplent la moitié d'un catalogue.
 */
export function isStockTracked(
  item: Pick<CatalogItem, "stockQuantity">,
): boolean {
  return item.stockQuantity != null;
}

/**
 * Le stock est-il en négatif ?
 *
 * Une sortie peut faire passer le solde sous zéro, et c'est **voulu** : le
 * matériel a physiquement quitté le dépôt, et refuser l'écriture obligerait le
 * patron à mentir à son outil. Un solde négatif n'est donc pas une erreur de
 * l'application — c'est la preuve que l'inventaire est faux, et l'écran doit
 * le dire plutôt que de l'arrondir à zéro.
 */
export function isStockNegative(
  item: Pick<CatalogItem, "stockQuantity">,
): boolean {
  return item.stockQuantity != null && item.stockQuantity < 0;
}

/**
 * Épuisé : il ne reste rien.
 *
 * Le patron, le 18 septembre : « zéro en stock n'est pas "stock bas" (orange)
 * mais épuisé (rouge) ». Il a raison, et la nuance n'est pas cosmétique :
 * sous le seuil, le chantier part quand même et la commande peut attendre
 * demain ; à zéro, le camion part à vide. Deux états, deux couleurs.
 *
 * Indépendant du seuil, à la différence d'`isLowStock` : un article compté à
 * zéro est épuisé, qu'on ait réglé une alerte ou non. C'est bien la quantité
 * qui doit être suivie — `null` veut toujours dire « pas compté », jamais
 * « il n'y en a plus ».
 */
export function isOutOfStock(item: Pick<CatalogItem, "stockQuantity">): boolean {
  return item.stockQuantity != null && item.stockQuantity <= 0;
}

/** Ce qu'un écran a besoin de savoir d'un article, en un mot. */
export type StockState = "non_suivi" | "epuise" | "bas" | "suffisant";

/**
 * L'état du stock d'un article. L'ordre des tests est l'ordre de gravité :
 * un article à zéro **et** sous son seuil est épuisé, pas bas.
 */
export function stockState(
  item: Pick<CatalogItem, "stockQuantity" | "reorderThreshold">,
): StockState {
  if (!isStockTracked(item)) return "non_suivi";
  if (isOutOfStock(item)) return "epuise";
  if (isLowStock(item)) return "bas";
  return "suffisant";
}

/**
 * À racheter : épuisé, ou sous le seuil.
 *
 * `isLowStock` seul laissait passer l'article compté à zéro sans seuil réglé —
 * celui qu'on a le plus besoin de racheter, et le seul dont on soit certain
 * qu'il manque.
 */
export function needsRestock(
  item: Pick<CatalogItem, "stockQuantity" | "reorderThreshold">,
): boolean {
  return isOutOfStock(item) || isLowStock(item);
}
